/**
 * `/admin` for a Platform Admin: provisioning tenants, and managing one.
 *
 * The two are addresses rather than tabs, so a tenant is linkable and the
 * master list's selection survives a reload.
 *
 * @module router/admin/PlatformAdminRoutes
 */

import React, { useMemo, useState } from "react";

import { Outlet, useNavigate, useParams } from "react-router";

import { CreateTenantForm } from "@/components/platform/CreateTenantForm";
import {
  matchesTenant,
  sortTenants,
  type TenantSort,
} from "@/components/platform/tenant-directory";
import { TenantDetail } from "@/components/platform/TenantDetail";
import { TenantFilter, TenantList } from "@/components/platform/TenantList";
import { TenantUserList } from "@/components/platform/TenantUserList";
import { useCreateTenant } from "@/components/platform/useCreateTenant";
import { useImpersonationAction } from "@/components/platform/useImpersonationAction";
import { useTenantDirectory } from "@/components/platform/useTenantDirectory";
import { useTenantUserList } from "@/components/platform/useTenantUserList";
import {
  Button,
  ErrorAlert,
  LinkTabs,
  MasterDetail,
  PageHeader,
  WarningAlert,
} from "@/components/ui";
import { ADMIN_AREA } from "@/router/admin/admin-area";
import { useWorkspaceOutletContext } from "@/router/workspace-context";

/** The two sections, in the order the console lists them. */
export const PLATFORM_SECTIONS = [
  { to: "/admin/manage", label: "Manage tenants", "data-testid": "tab-manage-tenant" },
  { to: "/admin/create", label: "Create tenant", "data-testid": "tab-create-tenant" },
] as const;

export function PlatformAdminLayout(): React.ReactElement {
  return (
    <div className="space-y-6" data-testid="platform-admin-console">
      <PageHeader
        title={ADMIN_AREA.platform}
        description="Set up new organisations and manage existing ones."
      />
      <LinkTabs items={PLATFORM_SECTIONS} aria-label="Platform console sections" />
      <Outlet />
    </div>
  );
}

export function PlatformCreateTenantRoute(): React.ReactElement {
  const { client } = useWorkspaceOutletContext();
  // Nothing to reload: the directory lives on the other route, which reads it
  // on mount, so listing every tenant here would be a request for a screen
  // that shows none of them.
  const creation = useCreateTenant(client, async () => undefined);
  return (
    <CreateTenantForm
      pending={creation.pending}
      error={creation.error}
      created={creation.created}
      onCreate={creation.create}
      onErrorCleared={creation.clearError}
    />
  );
}

export function PlatformManageTenantsRoute(): React.ReactElement {
  const { client } = useWorkspaceOutletContext();
  const navigate = useNavigate();
  const routeTenantId = useParams<{ tenantId?: string }>().tenantId;
  const directory = useTenantDirectory(client);
  const UserAction = useImpersonationAction();
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<TenantSort>("name");

  const visible = useMemo(
    () =>
      sortTenants(
        directory.tenants.filter((tenant) => matchesTenant(tenant, query)),
        sort,
      ),
    [directory.tenants, query, sort],
  );

  // The tenant in view is whatever the route names: `MasterDetail` opens the
  // first on wide screens by navigating. Matching every tenant, not the
  // visible ones, means filtering the chosen one out never reselects.
  const routed = directory.tenants.find((t) => t.id === routeTenantId);
  const named = routed !== undefined;
  const selectedTenantId = routed?.id ?? "";
  // An unread directory knows of no tenant, so it cannot say one is gone.
  const unknownTenant = Boolean(routeTenantId) && !named && !directory.loading && !directory.error;
  const selectedTenant = routed ?? null;
  const users = useTenantUserList(client, selectedTenantId);

  let emptyMessage: string;
  if (directory.tenants.length) {
    emptyMessage = "No tenants match this filter";
  } else if (directory.error) {
    emptyMessage = "The tenant directory could not be loaded";
  } else {
    emptyMessage = "No tenants are provisioned yet";
  }

  return (
    <div className="space-y-4">
      {directory.error ? (
        <ErrorAlert data-testid="platform-console-directory-error">
          <span className="flex flex-wrap items-center gap-3">
            {directory.error}
            <Button
              size="sm"
              variant="outline"
              icon="arrow-clockwise"
              onClick={() => {
                directory.reload().catch(() => undefined);
              }}
            >
              Try again
            </Button>
          </span>
        </ErrorAlert>
      ) : null}
      {unknownTenant ? (
        <WarningAlert data-testid="platform-console-unknown-tenant">
          No tenant has been set up at this address.
        </WarningAlert>
      ) : null}
      <MasterDetail
        label="Tenants"
        // Above `lg` both panes show, so the detail is never unreachable; below
        // it the route decides which one is on screen.
        selected={Boolean(routeTenantId)}
        onBack={() => {
          navigate("/admin/manage");
        }}
        backLabel="Back to tenants"
        data-testid="platform-console"
        autoSelectFirst={{
          firstId: visible[0]?.id,
          onSelect: (tenantId) => {
            navigate(`/admin/manage/${tenantId}`, { replace: true });
          },
        }}
        masterHeader={
          <TenantFilter
            total={directory.tenants.length}
            shown={visible.length}
            loading={directory.loading}
            query={query}
            onQueryChange={setQuery}
            sort={sort}
            onSortChange={setSort}
          />
        }
        master={
          <TenantList
            tenants={visible}
            loading={directory.loading}
            emptyMessage={emptyMessage}
            selectedTenantId={selectedTenantId}
            onSelect={(tenantId) => {
              navigate(`/admin/manage/${tenantId}`);
            }}
          />
        }
        detail={
          <div className="space-y-6">
            <TenantDetail
              tenant={selectedTenant}
              loading={directory.loading}
              entitlementSaving={directory.entitlementSavingTenantId === selectedTenantId}
              entitlementError={
                directory.entitlementError?.tenantId === selectedTenantId
                  ? directory.entitlementError.message
                  : null
              }
              onEntitlementChange={directory.setPublicCurriculumApiEnabled}
              reissuing={directory.reissuing}
              reissueError={directory.reissueError}
              onReissue={directory.reissue}
              onReissueClosed={directory.clearReissueError}
              unreadable={Boolean(directory.error)}
            />
            {/* The users belong to a tenant, so with none selected the panel
                has nothing to report on and must not say it found none. */}
            {selectedTenantId ? (
              <TenantUserList
                users={users.users}
                loading={users.loading}
                error={users.error}
                onRetry={users.reload}
                UserAction={UserAction}
              />
            ) : null}
          </div>
        }
      />
    </div>
  );
}
